// 利用者の一覧と検索。登録は事業所管理者・法人担当者だけに出す（API 側でも判定される）。
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { ChevronRight, Plus, Search } from 'lucide-react';
import { CARE_LEVEL } from '@alfine/shared/forms';
import { api, officePath } from '../lib/api';
import { canManageOffice, matchClient } from '../lib/domain';
import { messageOf } from '../lib/errors';
import { useOffice } from '../lib/office';
import { useLoad } from '../lib/useLoad';
import type { Client, Items } from '../lib/types';
import { NeedOffice } from '../components/Shell';
import { Button, Card, Dialog, ErrorText, Input, Loading, Muted, PageTitle } from '../components/ui';


function RegisterForm({ officeId, onCreated }: { officeId: string; onCreated: (id: string) => void }) {
  const [name, setName] = useState('');
  const [kana, setKana] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [careLevel, setCareLevel] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const body: Record<string, string> = { name: name.trim() };
      if (kana.trim()) body.kana = kana.trim();
      if (birthDate) body.birthDate = birthDate;
      if (careLevel) body.careLevel = careLevel;
      const r = await api<{ id: string }>(officePath(officeId, 'clients'), { officeId, body });
      onCreated(r.id);
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <label className="block space-y-1">
        <span className="text-sm text-ink-2">氏名（必須）</span>
        <Input data-testid="client-name" required maxLength={100} value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label className="block space-y-1">
        <span className="text-sm text-ink-2">ふりがな</span>
        <Input maxLength={100} value={kana} onChange={(e) => setKana(e.target.value)} />
      </label>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block space-y-1">
          <span className="text-sm text-ink-2">生年月日</span>
          <Input type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} />
        </label>
        <label className="block space-y-1">
          <span className="text-sm text-ink-2">要介護度</span>
          <select value={careLevel} onChange={(e) => setCareLevel(e.target.value)} className="h-10 w-full rounded-ui border border-line bg-surface px-2 text-ink-1">
            <option value="">未設定</option>
            {CARE_LEVEL.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </label>
      </div>
      <ErrorText>{error}</ErrorText>
      <div className="flex justify-end">
        <Button data-testid="client-register-submit" type="submit" disabled={saving || !name.trim()}>
          {saving ? '登録中…' : '登録する'}
        </Button>
      </div>
    </form>
  );
}

function ClientList({ officeId }: { officeId: string }) {
  const { me } = useOffice();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const { data, error, loading } = useLoad(() => api<Items<Client>>(officePath(officeId, 'clients'), { officeId }).then((r) => r.items), [officeId]);
  const shown = useMemo(() => (data ?? []).filter((c) => matchClient(c, q)), [data, q]);
  const canRegister = canManageOffice(me, officeId);

  return (
    <>
      <PageTitle
        actions={
          canRegister ? (
            <Button data-testid="client-register-open" onClick={() => setOpen(true)}>
              <Plus size={16} />
              利用者を登録
            </Button>
          ) : null
        }
      >
        利用者
      </PageTitle>
      <div className="relative mb-4">
        <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-4" />
        <Input data-testid="client-search" className="pl-9" placeholder="利用者名で検索" value={q} onChange={(e) => setQ(e.target.value)} aria-label="利用者名で検索" />
      </div>
      <ErrorText>{error}</ErrorText>
      {loading ? <Loading /> : null}
      {data && shown.length === 0 ? <Muted>{data.length === 0 ? '利用者はまだ登録されていません。' : '該当する利用者はいません。'}</Muted> : null}
      {shown.length > 0 ? (
        <Card className="divide-y divide-line p-0">
          {shown.map((c) => (
            <Link key={c.id} to={`/clients/${encodeURIComponent(c.id)}`} data-testid="client-row" className="flex min-h-11 items-center gap-3 px-4 py-2.5 hover:bg-subtle">
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-ink-1">{c.name}</div>
                {c.kana ? <div className="truncate text-xs text-ink-3">{c.kana}</div> : null}
              </div>
              <span className="text-sm text-ink-3">{c.careLevel ?? ''}</span>
              <ChevronRight size={16} className="text-ink-4" />
            </Link>
          ))}
        </Card>
      ) : null}
      <Dialog open={open} onClose={() => setOpen(false)} title="利用者を登録">
        <RegisterForm
          officeId={officeId}
          onCreated={(id) => {
            setOpen(false);
            navigate(`/clients/${encodeURIComponent(id)}`);
          }}
        />
      </Dialog>
    </>
  );
}

export function ClientsPage() {
  return <NeedOffice>{(officeId) => <ClientList key={officeId} officeId={officeId} />}</NeedOffice>;
}
